import type { IFetcher } from '@monofe/util';
import type { IMediaAPI } from '../../api/interface';
import type { IWorkspaceDB } from '../../db/interface';
import type { ILanguageDto, ITourDto } from '../../model/interface';
import type { IMediaCacheController } from '../../util/interface';
import type { IAuthenticationManager } from '../interface/auth';
import type { IMediaManager, MediaBinaryStorageTypeMap } from '../interface/media';
import mediaDownload from './media.download';
import mediaFilter from './media.filter';

async function _resolveUrlWithToken(
    database: Readonly<IWorkspaceDB>,
    auth: Readonly<IAuthenticationManager>,
    apis: Readonly<Readonly<IMediaAPI>[]>,
    fetcher: IFetcher,
    cacheController: Readonly<IMediaCacheController>,
    mediaId: string,
): Promise<string> {
    /* Short circuit, if we can just get the asset from cache */
    const isCached = await cacheController.isMediumCached(mediaId);

    if (isCached) {
        const url = mediaDownload.downloadToUrl(
            database,
            auth,
            cacheController,
            apis,
            mediaId,
            true,
        );
        return url;
    }

    const authToken = await auth.renew();

    let url: string | undefined = '';

    for (const api of apis) {
        const canResolve = await api.canResolveMedium(mediaId);

        if (!canResolve) {
            continue;
        }

        const urls = api.requestURL(mediaId, { accessToken: authToken });

        url = urls.find(item => item.length > 0);

        if (url !== undefined) {
            break;
        }
    }

    if (url === undefined) {
        throw new Error(`Unable to resolve mediaId: ${mediaId}`);
    }

    return url;
}

function mediaManager(
    database: Readonly<IWorkspaceDB>,
    auth: Readonly<IAuthenticationManager>,
    mediaAPI: () => IMediaAPI[],
    fetcher: IFetcher,
    cacheController: Readonly<IMediaCacheController>,
): IMediaManager {
    const manager: IMediaManager = {
        downloadToURL: (id: string, cacheOnly?: boolean) =>
            mediaDownload.downloadToUrl(
                database,
                auth,
                cacheController,
                mediaAPI(),
                id,
                cacheOnly ?? false,
            ),
        downloadToURLs: (ids: string[], cacheOnly?: boolean) =>
            mediaDownload.downloadToUrls(
                database,
                auth,
                cacheController,
                mediaAPI(),
                ids,
                cacheOnly ?? false,
            ),

        downloadToBinary: <
            T extends keyof MediaBinaryStorageTypeMap,
            U extends MediaBinaryStorageTypeMap[T],
        >(
            id: string,
            type?: T,
            cacheOnly?: boolean,
        ) =>
            mediaDownload.downloadToBinary<T, U>(
                database,
                auth,
                cacheController,
                mediaAPI(),
                id,
                cacheOnly ?? false,
                type,
            ),

        downloadToBinaries: <
            T extends keyof MediaBinaryStorageTypeMap,
            U extends MediaBinaryStorageTypeMap[T],
        >(
            ids: string[],
            type?: T,
            cacheOnly?: boolean,
        ) =>
            mediaDownload.downloadToBinaries<T, U>(
                database,
                auth,
                cacheController,
                mediaAPI(),
                ids,
                cacheOnly ?? false,
                type,
            ),

        isMediumCached: (mediaId: string) => cacheController.isMediumCached(mediaId),
        isAllMediaCached: (mediaIds: string[]) => cacheController.isAllMediaCached(mediaIds),
        removeMediumCache: (mediaId: string) => cacheController.removeMediumCache(mediaId),

        mediaForLanguage: (language: Readonly<ILanguageDto>) =>
            mediaFilter.mediaUsedForLanguage(database, language),
        mediaForLanguageWithTour: (language: Readonly<ILanguageDto>, tour: Readonly<ITourDto>) =>
            mediaFilter.mediaForLanguageWithTour(database, language, tour),

        resolveUrlWithToken: (mediaId: string) =>
            _resolveUrlWithToken(database, auth, mediaAPI(), fetcher, cacheController, mediaId),
    };

    return manager;
}

export default mediaManager;
